import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  LoadingSkeleton,
  Modal,
  Select,
  useToast,
} from '@nexora/design-system';
import { useBoms, type Bom } from './useBoms';
import {
  useBomAttachments,
  useCreateBomAttachment,
  useDeleteBomAttachment,
  type BomAttachment,
} from './useBomDetails';
import { formatDateTime } from '../../shared/utils/format';
import { uuidv4 } from '../../shared/utils/uuid';

/* ── Contrato (backend: bom-details.controller.ts + manufacturing-extended.dto.ts) ──
 *
 * GET    /manufacturing/boms/:id/attachments -> BomAttachmentResponseDto[]
 * POST   /manufacturing/boms/:id/attachments body: CreateBomAttachmentRequestDto
 *        { fileName, fileSize, mimeType?, storageKey } -> BomAttachmentResponseDto
 * DELETE /manufacturing/boms/:id/attachments/:attachmentId -> 204
 *
 * Tela prototipo "BOM - Anexos Tecnicos": File Name | Size | Uploaded | Actions,
 * area de upload e indicador de uso de armazenamento.
 *
 * Fidelidade: o backend registra METADADOS do anexo (JSON) — nao aceita multipart/
 * FormData. O input de arquivo le o nome/tamanho/tipo do arquivo local e registra o
 * metadata com um storageKey gerado; o conteudo em si nao e enviado por esta rota.
 * "Storage Usage" e calculado da soma dos fileSize reais, agrupado por tipo; o limite
 * de armazenamento do prototipo (100 MB) nao existe no contrato — omitido.
 */

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function mimeGroup(mime: string): { key: string; label: string } {
  if (mime === 'application/pdf') return { key: 'pdf', label: 'PDFs' };
  if (mime.startsWith('image/')) return { key: 'image', label: 'Imagens' };
  if (
    mime.includes('cad') ||
    mime.includes('stl') ||
    mime.includes('step') ||
    mime.includes('iges') ||
    mime.includes('dxf')
  ) {
    return { key: 'cad', label: 'CAD Models' };
  }
  return { key: 'doc', label: 'Documentos' };
}

export function BomAttachmentsPage(): JSX.Element {
  const { addToast } = useToast();
  const params = useParams({ strict: false }) as Record<string, string | undefined>;
  const routeId = params['bomId'] ?? params['id'];

  const [selectedId, setSelectedId] = useState<string>(routeId ?? '');
  useEffect(() => {
    if (routeId) setSelectedId(routeId);
  }, [routeId]);

  const { data: bomsData } = useBoms({ page: 1, limit: 100 });
  const boms = bomsData?.data ?? [];

  const { data: attachments, isLoading, error, refetch } = useBomAttachments(selectedId || undefined);
  const createAtt = useCreateBomAttachment();
  const deleteAtt = useDeleteBomAttachment();

  const [confirmDelete, setConfirmDelete] = useState<BomAttachment | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File | undefined): Promise<void> => {
    if (!file || !selectedId) return;
    if (file.size <= 0) {
      addToast('Arquivo vazio (0 bytes)', 'error');
      return;
    }
    setUploading(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      await createAtt.mutateAsync({
        id: selectedId,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type || 'application/octet-stream',
        storageKey: `bom/${selectedId}/${uuidv4()}-${safeName}`,
      });
      addToast(`Anexo "${file.name}" registrado`, 'success');
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Erro ao registrar anexo', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!confirmDelete || !selectedId) return;
    try {
      await deleteAtt.mutateAsync({ id: selectedId, attachmentId: confirmDelete.id });
      addToast('Anexo removido', 'success');
      setConfirmDelete(null);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Erro ao remover anexo', 'error');
    }
  };

  const storage = useMemo(() => {
    const items = attachments ?? [];
    const total = items.reduce((s, a) => s + a.fileSize, 0);
    const groups = new Map<string, { label: string; bytes: number }>();
    for (const a of items) {
      const g = mimeGroup(a.mimeType);
      const cur = groups.get(g.key) ?? { label: g.label, bytes: 0 };
      cur.bytes += a.fileSize;
      groups.set(g.key, cur);
    }
    const maxPct = total > 0 ? Math.max(...[...groups.values()].map((g) => (g.bytes / total) * 100)) : 0;
    return { total, groups: [...groups.entries()], maxPct };
  }, [attachments]);

  return (
    <div className="bom-att-page">
      <style>{`
        .bom-att-page { padding: 1.5rem; }
        .bom-att-table-wrap { overflow-x: auto; }
        .bom-att-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .bom-att-table th { text-align: left; padding: 0.5rem 0.75rem; font-weight: 600; color: #43474e; border-bottom: 1px solid #c4c6cf; background: #eceef0; white-space: nowrap; }
        .bom-att-table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #e0e3e5; }
        .bom-att-num { text-align: right; font-variant-numeric: tabular-nums; font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .bom-att-empty { padding: 2rem 1rem; text-align: center; color: #43474e; }
        .bom-att-error { padding: 1.5rem; text-align: center; color: #ba1a1a; }
        .bom-att-grid { display: grid; grid-template-columns: repeat(12, 1fr); gap: 1rem; }
        .bom-att-col-upload { grid-column: span 12; }
        .bom-att-upload { border: 2px dashed #c4c6cf; border-radius: 0.5rem; padding: 1.5rem; text-align: center; cursor: pointer; background: #f7f9fb; }
        .bom-att-upload:hover { background: #f2f4f6; }
        .bom-att-storage-bar { height: 0.625rem; border-radius: 999px; background: #e0e3e5; overflow: hidden; display: flex; }
        @media (min-width: 1280px) {
          .bom-att-col-upload { grid-column: span 4; }
          .bom-att-col-list { grid-column: span 8; }
        }
      `}</style>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <p className="font-label-sm text-label-sm text-on-surface-variant mb-xs uppercase tracking-wider">
            Produção / Estrutura de Produto (BOM)
          </p>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Anexos Técnicos</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Datasheets, desenhos técnicos e certificados da estrutura de produto.
          </p>
        </div>
      </div>

      {!routeId && (
        <Card variant="outlined" padding="md" className="mb-lg">
          <div style={{ minWidth: '280px', maxWidth: '480px' }}>
            <Select
              label="Estrutura (BOM)"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              <option value="">— Selecionar —</option>
              {boms.map((b: Bom) => (
                <option key={b.id} value={b.id}>
                  {b.description} (v{b.version})
                </option>
              ))}
            </Select>
          </div>
        </Card>
      )}

      {isLoading && (
        <Card padding="1rem">
          <LoadingSkeleton count={6} height="48px" />
        </Card>
      )}

      {error && !isLoading && (
        <Card padding="2rem">
          <div className="bom-att-error" role="alert">
            <p>Erro ao carregar os anexos</p>
            <p className="font-body-sm text-body-sm text-on-surface-variant">{(error as Error).message}</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        </Card>
      )}

      {!isLoading && !error && !selectedId && (
        <Card padding="2rem">
          <div className="bom-att-empty">Nenhuma estrutura selecionada.</div>
        </Card>
      )}

      {selectedId && !isLoading && !error && (
        <div className="bom-att-grid">
          <div className="bom-att-col-list">
            <Card padding="0">
              <CardHeader
                className="px-md pt-md"
                action={
                  <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                    {(attachments ?? []).length} {(attachments ?? []).length === 1 ? 'arquivo' : 'arquivos'}
                  </span>
                }
              >
                <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Arquivos</h3>
              </CardHeader>
              {(attachments ?? []).length === 0 ? (
                <div className="bom-att-empty">
                  Nenhum anexo registrado. Use a área de upload ao lado para registrar o metadata de um arquivo.
                </div>
              ) : (
                <div className="bom-att-table-wrap">
                  <table className="bom-att-table">
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th style={{ textAlign: 'right' }}>Size</th>
                        <th>Uploaded</th>
                        <th style={{ textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(attachments ?? []).map((a) => (
                        <tr key={a.id}>
                          <td>
                            <span className="font-medium text-on-surface">{a.fileName}</span>
                            <span className="font-label-sm text-label-sm text-on-surface-variant ml-xs">
                              {a.mimeType}
                            </span>
                          </td>
                          <td className="bom-att-num">{formatFileSize(a.fileSize)}</td>
                          <td className="text-on-surface-variant">{formatDateTime(a.createdAt)}</td>
                          <td style={{ textAlign: 'right' }}>
                            <Button size="sm" variant="danger" onClick={() => setConfirmDelete(a)}>
                              Excluir
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          <div className="bom-att-col-upload">
            {/* Upload (metadata) */}
            <Card padding="md">
              <div className="bom-att-upload" onClick={() => fileInputRef.current?.click()}>
                <p className="font-headline-sm text-headline-sm text-primary m-0 mb-xs">
                  Upload New Document
                </p>
                <p className="font-body-sm text-body-sm text-on-surface-variant m-0 mb-md">
                  Selecione um arquivo para registrar o anexo (nome, tamanho e tipo). O
                  conteúdo não é transmitido por esta rota — o backend registra metadados.
                </p>
                <Button
                  size="sm"
                  loading={uploading}
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  Browse Files
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={(e) => void handleFile(e.target.files?.[0])}
                />
              </div>
            </Card>

            {/* Storage Usage (calculado dos fileSize reais) */}
            <Card padding="md" className="mt-md">
              <div className="flex justify-between items-center mb-sm">
                <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Storage Usage</h3>
                <Badge variant="neutral" size="sm">{formatFileSize(storage.total)}</Badge>
              </div>
              {storage.total > 0 ? (
                <>
                  <div className="bom-att-storage-bar mb-sm">
                    {storage.groups.map(([key, g]) => (
                      <div
                        key={key}
                        title={`${g.label}: ${formatFileSize(g.bytes)}`}
                        style={{
                          width: `${(g.bytes / storage.total) * 100}%`,
                          background: key === 'pdf' ? '#ba1a1a' : key === 'cad' ? '#002045' : '#505f76',
                          height: '100%',
                        }}
                      />
                    ))}
                  </div>
                  <ul className="font-body-sm text-body-sm" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {storage.groups.map(([key, g]) => (
                      <li
                        key={key}
                        className="flex justify-between items-center text-on-surface-variant"
                        style={{ padding: '0.25rem 0' }}
                      >
                        <span className="flex items-center gap-xs">
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 999,
                              background: key === 'pdf' ? '#ba1a1a' : key === 'cad' ? '#002045' : '#505f76',
                              display: 'inline-block',
                            }}
                          />
                          {g.label}
                        </span>
                        <span>{formatFileSize(g.bytes)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="font-body-sm text-body-sm text-on-surface-variant m-0">
                  Nenhum anexo — uso de armazenamento em 0.
                </p>
              )}
            </Card>
          </div>
        </div>
      )}

      {/* Confirmacao de exclusao */}
      <Modal
          isOpen={!!confirmDelete}
          onClose={() => setConfirmDelete(null)}
          title="Excluir anexo"
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
              <Button variant="danger" onClick={() => void handleDelete()} loading={deleteAtt.isPending}>
                Excluir
              </Button>
            </>
          }
        >
          <p className="font-body-md text-body-md text-on-surface m-0">
            Remover o anexo <strong>{confirmDelete?.fileName}</strong> desta estrutura?
          </p>
        </Modal>
    </div>
  );
}